import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setUser } from '../../store/shared/userSlice';
import AuthService from '../../services/AuthService';
import { useLazyGetAccountListQuery } from '../../../Admin/AccountSelect/apiSlice';

function SigninCallback() {
  const navigate = useNavigate();
  const authService = new AuthService();
  const dispatch = useDispatch();

  const [getAccountList] = useLazyGetAccountListQuery();

  useEffect(() => {
    (async () => {
      let user: any;
      // Handle popup log in — only complete the OIDC handshake here.
      // The main window reloads after loginPopup() resolves, which re-triggers
      // ProtectedHubRoute / AdminPortalInitializer to re-run their B2B/2FA
      // enrichment flows and obtain a token with the required claims.
      if (
        window.opener &&
        window.opener !== window &&
        !window.menubar.visible
      ) {
        try {
          await authService.loginPopupCallback();
        } catch (error) {
          window.close();
        }
        return;
      }
      // Circuit-breaker: if signinCallback keeps failing, stop redirecting to
      // avoid an infinite loop. Three failures in a row means something is
      // structurally wrong (e.g. corrupted OIDC state store).
      const REDIRECT_COUNT_KEY = 'signinRedirectCount';
      try {
        user = await authService.signinCallback();
        sessionStorage.removeItem(REDIRECT_COUNT_KEY);
        dispatch(setUser({ ...user }));
      } catch (e) {
        console.error(e);
        const redirectCount = parseInt(
          sessionStorage.getItem(REDIRECT_COUNT_KEY) || '0',
          10,
        );
        if (redirectCount >= 3) {
          sessionStorage.removeItem(REDIRECT_COUNT_KEY);
          try {
            await authService.logout();
          } catch {
            navigate('/');
          }
          return;
        }
        sessionStorage.setItem(REDIRECT_COUNT_KEY, String(redirectCount + 1));
        authService.login('/');
        return;
      }

      // Navigation — only reached when signinCallback() succeeded.
      // Kept out of a finally block deliberately: finally runs even after a
      // return in catch, which would call authService.login('/') twice and
      // corrupt the OIDC state store, causing the very loop we're preventing.
      if (user?.state?.originalUrl?.length > 1) {
        navigate(user.state.originalUrl);
      } else {
        const currentTimeUtc = Math.floor(new Date().getTime() / 1000);
        if (!user || !user.access_token || currentTimeUtc >= user.expires_at) {
          authService.login('/');
        } else {
          getAccountList({
            page: 1,
            pageSize: 1,
            token: user?.access_token,
          })
            .unwrap()
            .then((accountList) => {
              if (
                !accountList?.accounts ||
                accountList?.accounts?.totalItemCount <= 0
              ) {
                navigate('/hub');
                return;
              }
              navigate('/admin');
            })
            .catch(() => navigate('/hub'));
        }
      }
    })();
  }, []);

  return <h1 className="p-5">Signing in...</h1>;
}

export default SigninCallback;
